'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

/* eslint-disable no-case-declarations */
/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Form,
  Label,
  Input,
  FormGroup,
  Alert,
  Modal,
  InputProps,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { MdCheck, MdErrorOutline, MdInfoOutline } from 'react-icons/md';
import { getCookieConsentValue } from 'react-cookie-consent';
import dayjs from '../utils/dayjs';
import PhoneInput, { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import { useSessionStorage } from 'usehooks-ts';
import Login from 'shared/components/auth/Login';
import { uploadApplication } from '../api/application';
import { getLocationsForAutocomplete } from '../api/locations';
import { useUser } from '../contexts/UserContext';
import usePhoneInputLabelLanguage from '../hooks/usePhoneInputLabelLanguage';
import { addTalentPixel } from '../utils/addTalentPixel';
import capitalizeFirstLetter from '../utils/capitalizeFirstLetter';
import encodeBase64 from '../utils/encodeBase64';
import { useUtmValues } from '../hooks/useUtmTracking';
import { logSentryError } from '../utils/logSentry';
import { plausibleEvent } from '../utils/plausibleEvent';
import validateMail from '../utils/validateEmailAddress';
import { NAME_MAX_LENGTH } from '../utils/validateName';
import Cta from './CtaBtn';
import ResumeUploadField from './ResumeUploadField';

import 'react-phone-number-input/style.css';
import { getI18nUtils } from '../i18n/i18n-utils';
import { Dictionaries, Locale } from '../i18n/i18n-config';
import Link from 'next/link';

const allowedCvFileTypes = ['.doc', '.docx', '.pdf', '.rtf'];

type InputConfig = {
  type: InputProps['type'];
  value: any;
  touched: boolean;
  valid: boolean;
  minLength?: number;
  maxLength?: number;
  intlId: keyof Dictionaries;
  order: number;
  required: boolean;
  disabled?: boolean;
};

const defaultInputs: Record<string, InputConfig> = {
  firstName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'job-detail-form-firstname',
    order: 1,
    required: true,
  },
  lastName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'job-detail-form-lastname',
    order: 2,
    required: true,
  },
  email: {
    type: 'email',
    value: '',
    touched: false,
    valid: false,
    intlId: 'job-detail-form-email',
    order: 3,
    required: true,
    minLength: 5,
  },
  telephone: {
    type: 'tel',
    value: '',
    touched: false,
    valid: false,
    intlId: 'job-detail-form-phone',
    order: 4,
    required: true,
  },
  birthDate: {
    type: 'date',
    value: '',
    touched: false,
    valid: false,
    intlId: 'job-detail-form-birthdate',
    order: 5,
    required: true,
  },
};

const defaultCvInput = { type: 'file', file: null, name: null, size: null };
type DefaultCvInput = { type: string; file: string | ArrayBuffer | null; name: null | string; size: null | number };

function ApplicationForm({
  jobId,
  withPostalCode,
  title,
  dictionary,
}: {
  jobId: string;
  withPostalCode?: boolean;
  title?: string;
  dictionary: Partial<Dictionaries>;
}): JSX.Element {
  const { push } = useRouter();
  const { locale }: { locale: Locale } = useParams();
  const { formatMessage } = getI18nUtils(dictionary);
  const { candidate, refetchCandidate, user, isLoggedIn } = useUser();
  const phoneLabelLanguage = usePhoneInputLabelLanguage();
  const [inputs, setInputs] = useState(defaultInputs);
  const [cv, setCv] = useState<DefaultCvInput>(defaultCvInput);
  const [location, setLocation] = useState<{ city: string; postalCode: string; lat: string; lng: string } | null>(null);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [formIsValid, setFormIsValid] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [fileError, setFileError] = useState<null | string>(null);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [requireLogin, setRequireLogin] = useState<boolean>(false);
  const [sessionLastApplication, setSessionLastApplication] = useSessionStorage<string | null>('latestJobApplication', null);
  const utmValues = useUtmValues();

  async function onSubmitHandler(e) {
    e.preventDefault();
    setRequireLogin(false);
    setSubmitting(true);
    setErrorMessage(null);

    const { firstName, lastName, email, telephone, birthDate } = inputs;
    const body: any = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      telephone: telephone.value,
      birthDate: dayjs(birthDate.value, 'YYYY-MM-DD').valueOf(),
      language: locale,
      postalCode: location ? location.postalCode : null,
      location: location ? { lat: location.lat, lng: location.lng } : null,
      app: process.env.NEXT_PUBLIC_APP || 'synergiejobs.be',
      job: jobId,
      analytics: utmValues,
    };

    if (cv.file) {
      body.cvFile = encodeBase64(String(cv.file));
      body.cvName = cv.name;
      body.cvDate = Date.now();
    }

    try {
      await uploadApplication(body);
      plausibleEvent('apply');
      const latestJobApplication = {
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        telephone: telephone.value,
        birthDate: birthDate.value,
        cvDate: cv.file ? Date.now() : null,
      };

      if (isLoggedIn) await refetchCandidate();
      else setSessionLastApplication(JSON.stringify(latestJobApplication)); // use session storage for thankyou page

      setSubmitting(false);
      if (getCookieConsentValue() === 'true') addTalentPixel(utmValues.utm_source);
      setSubmitted(true);
      push(`/${locale}/${body.app === 'sandyou.be' ? 'jobs/' : ''}thankyou/${jobId}/`);
    } catch (error) {
      setSubmitting(false);
      const apiMessage: string = error?.response?.data?.message || '';
      if (error?.response?.data?.registeredUser) setRequireLogin(true);
      else if (error?.response?.data?.code === 'email-verification-error')
        setErrorMessage(formatMessage({ id: 'email-verification-sent' }));
      else if (apiMessage === 'file-extension-not-allowed-for-upload' || apiMessage === 'file-content-not-allowed-for-upload') {
        // API weigert het CV (extensie of inhoud klopt niet): ander bestand laten kiezen
        const message = formatMessage({ id: 'file-extension-not-allowed-for-upload' });
        setFileError(message);
        setErrorMessage(message);
      } else if (apiMessage.startsWith('ERR_INVALID_JOB_ID')) setErrorMessage(formatMessage({ id: 'apply-job-unavailable' }));
      else {
        setErrorMessage(formatMessage({ id: 'internal-server-error' }));
        logSentryError(error);
      }
    }
  }

  // check validity for every inputType and terms
  const checkFormValidity = useCallback(() => {
    let validForm: boolean;
    if (withPostalCode) {
      validForm = (Object.keys(inputs).every((input) => inputs[input].valid) &&
        termsAccepted &&
        location &&
        !fileError) as boolean;
    } else validForm = (Object.keys(inputs).every((input) => inputs[input].valid) && termsAccepted && !fileError) as boolean;

    setFormIsValid(validForm);
  }, [inputs, location, termsAccepted, withPostalCode, fileError]);

  function handleCvUpload(uploadEvent: React.ChangeEvent<HTMLInputElement>) {
    setFileError(null);
    const file = uploadEvent?.target?.files?.[0];
    const extension = file ? file.name.substring(file.name.lastIndexOf('.')) : null;

    if (file && extension && allowedCvFileTypes.indexOf(extension.toLowerCase()) !== -1) {
      const Reader = new FileReader();
      Reader.onload = (readerEvent) => {
        if (readerEvent.target === null) return;
        setCv({ ...cv, file: readerEvent.target.result, name: file.name, size: file.size });
      };
      Reader.readAsDataURL(file);
    } else setFileError(formatMessage({ id: 'file-extension-not-allowed-for-upload' }));

    checkFormValidity();
  }

  // validate a specific input
  function isInputValid(input: InputConfig, inputKey: string) {
    const { value, required, minLength, maxLength } = input;
    if (required && !value) return false;
    if (minLength && value?.trim().length < minLength) return false; // API trimt
    if (maxLength && value?.length > maxLength) return false;
    switch (inputKey) {
      case 'email':
        return value && validateMail(value);
      case 'telephone':
        return value && isValidPhoneNumber(value);
      case 'birthDate':
        return value && dayjs(value).isValid();
      default:
        return true;
    }
  }

  // validate the form when an input changes
  useEffect(() => {
    checkFormValidity();
  }, [inputs, termsAccepted, location, checkFormValidity]);

  const memoizedSetUserInputs = useMemo(() => {
    const candidateData = candidate || (sessionLastApplication && JSON.parse(sessionLastApplication)) || null;
    const setUserInputs = () => {
      if (candidateData) {
        const inputsCp = { ...defaultInputs };
        Object.keys(candidateData).forEach((key) => {
          const hasprop = Object.prototype.hasOwnProperty.call(inputsCp, key);
          if (!hasprop) return;
          switch (key) {
            case 'telephone':
              const hasCountryCode =
                candidateData.id &&
                candidateData[key] &&
                (candidateData[key].startsWith('+') || candidateData[key].startsWith('00'));
              const value = candidateData[key] ? parsePhoneNumber(candidateData[key], 'BE')?.number : '';

              inputsCp[key] = {
                ...inputsCp[key],
                value,
                valid: !!(value && isValidPhoneNumber(value)),
                disabled: isLoggedIn && value && isValidPhoneNumber(value) && hasCountryCode,
              };
              break;
            case 'birthDate':
              const bdayValue = candidateData[key] ? dayjs(candidateData[key]).format('YYYY-MM-DD') : '';
              inputsCp[key] = {
                ...inputsCp[key],
                value: bdayValue,
                valid: !!bdayValue,
                disabled: isLoggedIn && !!bdayValue,
              };
              break;
            default:
              inputsCp[key] = {
                ...inputsCp[key],
                value: candidateData[key],
                valid: true,
                disabled: isLoggedIn,
              };
              break;
          }
        });
        setInputs(inputsCp);
        setTermsAccepted(true);
        setFormIsValid(true);
      } else {
        setInputs(defaultInputs);
        setCv(defaultCvInput);
        setTermsAccepted(false);
        setFormIsValid(false);
      }
    };
    return setUserInputs;
  }, [candidate, sessionLastApplication]);

  useEffect(() => memoizedSetUserInputs(), [candidate, sessionLastApplication]);

  function inputChangedHandler(e, inputKey: string) {
    setErrorMessage(null);
    const inputCp = { ...inputs[inputKey] };
    inputCp.touched = true;
    inputCp.value = inputKey === 'telephone' ? e : e.target.value;
    inputCp.valid = isInputValid(inputCp, inputKey);
    setInputs({ ...inputs, [inputKey]: inputCp });
  }

  const toggleModal = () => setInfoModalOpen((prev) => !prev);

  const customStyles = {
    control: (provided, state) => {
      let boxShadow = '';
      if (location && state.isFocused) boxShadow = '0 0 0 0.2rem rgba(40, 167, 69, 0.25)';
      if (!location && state.isFocused) boxShadow = '0 0 0 0.2rem rgba(226, 0, 26, 0.25)';

      let border = '';
      if (location) border = '1px solid #28a745';
      if (!location && state.isFocused) border = '1px solid red';

      return {
        ...provided,
        border,
        boxShadow,
      };
    },
  };

  return (
    <Card id="applicationForm" className={`mt-4${submitting ? ' submitting' : ''}`}>
      <CardBody>
        {submitted ? (
          <Row>
            <Col xs={1} className="me-3">
              <MdCheck size={24} color="#28a745" />
            </Col>
            <Col xs={10}>
              <CardTitle tag="h3" className="mb-2">
                {formatMessage({ id: 'application_form_thanks' })}
              </CardTitle>
              <p className="mb-0">{formatMessage({ id: 'application_form_thanks_subtitle' })}</p>
            </Col>
          </Row>
        ) : (
          <>
            <CardTitle tag="h2" className="mb-3 d-flex justify-content-between">
              <div>
                {title ||
                  capitalizeFirstLetter(
                    formatMessage({
                      id: withPostalCode ? 'job-detail-h1-apply-spontaniously' : 'job-detail-h2-apply',
                    })
                  )}
              </div>
              <div>
                <button type="button" aria-label="Info" className="btn btn-link text-black p-0" onClick={() => toggleModal()}>
                  <MdInfoOutline size={24} />
                </button>
              </div>
            </CardTitle>
            {requireLogin && !user ? (
              <>
                <p className="text-muted mb-4">{formatMessage({ id: 'require_login_info' })}</p>
                <Login inline locale={locale} dictionary={dictionary} emailAddress={inputs.email.value} />
              </>
            ) : (
              <Form onSubmit={(e) => onSubmitHandler(e)}>
                <fieldset disabled={submitting} aria-busy={submitting}>
                  <Row>
                    {Object.keys(inputs)
                      // important!:  to avoid an issue with ios, sort the inputs
                      .sort((a, b) => (inputs[a].order > inputs[b].order ? 1 : -1))
                      .filter((inputKey) => (withPostalCode ? true : inputKey !== 'postalCode'))
                      .map((inputKey) => {
                        const { value, touched, minLength, maxLength, valid, required, type, intlId, disabled } =
                          inputs[inputKey];

                        return (
                          <Col xs={12} md={6} key={inputKey}>
                            <FormGroup>
                              <Label className={`text-muted${required ? ' required' : ''}`} for={inputKey}>
                                {formatMessage({ id: intlId })}
                              </Label>
                              {inputKey === 'telephone' ? (
                                <PhoneInput
                                  className={`${touched && valid ? 'is-valid' : ''} ${touched && !valid ? 'is-invalid' : ''} ${
                                    submitting ? 'submitting' : ''
                                  }`}
                                  id={inputKey}
                                  disabled={disabled}
                                  name={inputKey}
                                  defaultCountry="BE"
                                  required={required}
                                  value={value}
                                  labels={phoneLabelLanguage}
                                  international
                                  countryCallingCodeEditable={false}
                                  onChange={(E164Number) => inputChangedHandler(E164Number, inputKey)}
                                  onBlur={() => checkFormValidity()}
                                />
                              ) : (
                                <Input
                                  id={inputKey}
                                  disabled={disabled}
                                  name={inputKey}
                                  maxLength={maxLength}
                                  minLength={minLength}
                                  required={required}
                                  value={value}
                                  valid={touched && valid}
                                  invalid={touched && !valid}
                                  type={type}
                                  onChange={(e) => inputChangedHandler(e, inputKey)}
                                  onBlur={() => checkFormValidity()}
                                />
                              )}
                            </FormGroup>
                          </Col>
                        );
                      })}
                    {withPostalCode && (
                      <Col xs="12" md="6">
                        <FormGroup>
                          <Label className="required text-muted">{formatMessage({ id: 'contact-form-postalCode' })}</Label>
                          <AsyncSelect
                            value={location}
                            loadOptions={getLocationsForAutocomplete}
                            getOptionLabel={(option) => `${option.postalCode} ${option.city}`}
                            getOptionValue={(option) => `${option.postalCode} ${option.city}`}
                            onChange={(newLocation) => setLocation(newLocation)}
                            id="location"
                            defaultOptions
                            placeholder={formatMessage({ id: 'contact-form-select-postalCode' })}
                            className={`react-select mb-3${location ? ' valid' : ''}`}
                            classNamePrefix="react-select"
                            styles={customStyles}
                            onBlur={checkFormValidity}
                          />
                        </FormGroup>
                      </Col>
                    )}
                    <Col xs="12" md="6">
                      <ResumeUploadField
                        dictionary={dictionary}
                        candidate={candidate as Candidate}
                        handleCvUpload={(e) => handleCvUpload(e)}
                        allowedCvFileTypes={allowedCvFileTypes}
                        valid={cv.file ? !fileError : false}
                        invalid={!!fileError}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <small className="d-block mb-2">{formatMessage({ id: 'application-form-registration-disclaimer' })}</small>
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            className="me-0"
                            id="terms"
                            name="terms"
                            onChange={() => {
                              setTermsAccepted(!termsAccepted);
                            }}
                            checked={termsAccepted}
                          />
                          <div className="ms-2">
                            {formatMessage(
                              { id: 'accept-privacy-terms' },
                              {
                                privacyLink: (
                                  <a key="privacy" href={`/${locale}/privacy`} target="_blank" rel="noopener noreferrer">
                                    {formatMessage({ id: 'privacy_policy' })}
                                  </a>
                                ),
                                termsLink: (
                                  <a key="terms" href={`/${locale}/termsofuse`} target="_blank" rel="noopener noreferrer">
                                    {formatMessage({ id: 'termsofuse' })}
                                  </a>
                                ),
                              }
                            )}
                          </div>
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                  {errorMessage && (
                    <Alert color="danger" className="d-flex align-items-center py-2">
                      <MdErrorOutline className="d-inline-block me-2" size="16" color="#842029" />
                      <span>{errorMessage}</span>
                    </Alert>
                  )}
                  <Cta className="cta red noArrow mt-2" type="submit" disabled={!formIsValid}>
                    {formatMessage({ id: 'job-detail-form-submit' })}
                  </Cta>
                </fieldset>
              </Form>
            )}
          </>
        )}
      </CardBody>
      <Modal isOpen={infoModalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>{capitalizeFirstLetter(formatMessage({ id: 'info' }))}</ModalHeader>
        <ModalBody>
          {formatMessage(
            { id: 'application-form-info-modal-text' },
            {
              profileLink: <Link href={`/${locale}/my-synergie/profile`}>{formatMessage({ id: 'here' })}</Link>,
            }
          )}
        </ModalBody>
      </Modal>
    </Card>
  );
}

export default ApplicationForm;
